import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan, faCarCrash, faWrench } from "@fortawesome/free-solid-svg-icons"
import { fetchDepVehicules } from "../../../store/actions/depannageActions"
import { fetchDispatchEnCours } from "../../../store/actions/depannageDispatchActions"
import { DEP_STATE_ONGOING } from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import {
	buildSecteurParCode,
	getZoneLabel,
} from "../../../utilities/depSecteurs"
import { DISPATCH_POLL_MS } from "./DepDispatchATraiter"
import { abonnerMissionsModifiees } from "../../../utilities/notificationsSocket"
import "./DepDispatch.css"

// Statuts PropreVehicule (miroir backend).
const STATUS_FONCTIONNEL = 0
const STATUS_ACCIDENTE = 1

const JALONS = ["timestamp_0", "timestamp_1", "timestamp_2", "timestamp_3"]

// Index du dernier jalon posé (-1 : aucun). C'est LA règle de mouvement :
// t0/t2 → en direction, t1/t3 → sur place (validée avec Jordan, 07.2026).
const dernierJalon = (dm) => {
	for (let i = JALONS.length - 1; i >= 0; i--) {
		if (dm[JALONS[i]]) return i
	}
	return -1
}

// "DD-MM-YYYY HH:MM" → "HH:MM" si aujourd'hui, sinon "DD.MM HH:MM".
const formatHeure = (ts) => {
	const m = moment(ts, "DD-MM-YYYY HH:mm")
	if (!m.isValid()) return ts
	return m.isSame(moment(), "day")
		? m.format("HH:mm")
		: m.format("DD.MM HH:mm")
}

// Hors service : « Indisponible » réglé dans la Flotte (QA Jordan 27.10,
// champ `indisponible`), sinon le statut PropreVehicule.
export const motifHs = (c) =>
	c.indisponible
		? { texte: "Indisponible", color: COLORS.textMuted, icon: faBan }
		: c.status === STATUS_ACCIDENTE
			? { texte: "Accidenté", color: COLORS.danger, icon: faCarCrash }
			: { texte: "En réparation", color: COLORS.warning, icon: faWrench }

// "Marque Modèle (plaques)" (get_nom backend) → { modele, plaques }.
export const parseNomCamion = (nom) => {
	const m = (nom || "").match(/^(.*)\s\(([^)]*)\)$/)
	return m
		? { modele: m[1], plaques: m[2] }
		: { modele: nom || "—", plaques: "" }
}

// Initiales du détenteur ("Nom Prénom" → "NP").
const initiales = (nom) =>
	(nom || "")
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((p) => p[0].toUpperCase())
		.join("")

// Cible du remorquage après t2 : destination intermédiaire si posée (POI ou
// textes libres), sinon destination finale.
const cibleApresLieu = (dm) => {
	const textesInterm = [
		dm.destination_intermediaire_0,
		dm.destination_intermediaire_1,
		dm.destination_intermediaire_2,
	]
	const interm = dm.destination_intermediaire
	if ((interm && interm.id) || textesInterm.some(Boolean))
		return { po: interm, textes: textesInterm }
	return {
		po: dm.destination_finale,
		textes: [
			dm.destination_finale_0,
			dm.destination_finale_1,
			dm.destination_finale_2,
		],
	}
}

const parseTs = (ts) => moment(ts, "DD-MM-YYYY HH:mm")

// Ordre des couloirs et tri interne « En mission » (du plus au moins urgent
// à surveiller). Les libellés longs servent au popover.
const LANES = [
	{ key: "mission", label: "En mission" },
	{ key: "libre", label: "Libres" },
	{ key: "hs", label: "Hors service" },
]
const KIND_ORDER = { recue: 0, route: 1, place: 2 }
const KIND_LONG = {
	recue: "Mission reçue",
	route: "En direction",
	place: "Sur place",
}

// Au-delà de ~2 rangées de puces, le couloir se replie sur « +N ».
const LANE_CAP = 18

// Bandeau « Camions » du dispatch : fermé par défaut sur sa seule ligne de
// compteurs — chaque pastille (En mission / Libres / Hors service) est un
// toggle qui déplie SON couloir sous la ligne (cumulables). Une puce compacte
// par camion, détail complet en popover au clic. Visible sur tous les onglets ;
// poll partagé avec la cadence du dispatch.
const DepDispatchCamions = () => {
	const dispatch = useDispatch()
	// Popover « détail camion » ouvert (id du camion) — un seul à la fois.
	const [popoverId, setPopoverId] = useState(null)
	// Couloirs dépliés ({ [clé de LANES]: true }) via les pastilles de
	// compteurs. Vide = bandeau fermé (l'état de départ).
	const [actifs, setActifs] = useState({})
	// Couloirs dépliés au-delà de LANE_CAP ({ [key]: true }).
	const [deplies, setDeplies] = useState({})
	const rootRef = useRef(null)
	const { depVehicules } = useSelector((state) => state.depInfo)
	const { depsEnCours } = useSelector((state) => state.depannageDispatchInfo)
	const enCoursRef = useRef(depsEnCours)
	enCoursRef.current = depsEnCours
	const secteurs = useSelector((state) => state.autreInfo.secteurs)

	useEffect(() => {
		dispatch(fetchDepVehicules())
		dispatch(fetchDispatchEnCours())
		const interval = setInterval(() => {
			dispatch(fetchDepVehicules())
			dispatch(fetchDispatchEnCours())
		}, DISPATCH_POLL_MS)
		// GE-62 (B3) : une mission écrite → rechargement (regroupé sur 600 ms)
		// des camions et des missions en cours, que lisent aussi l'onglet En
		// cours, son détail et « Compléter ». Le poll reste le filet.
		let regroupe = null
		const desabonner = abonnerMissionsModifiees((data) => {
			// Revue phase B (🟡 I) : seulement une mission en cours (30-59)
			// ou déjà affichée (elle vient d'en sortir), ou une reconnexion.
			const pertinente =
				data.reconnexion ||
				(data.etat >= 30 && data.etat < 60) ||
				enCoursRef.current.some((dm) => dm.id === data.id)
			if (!pertinente || regroupe) return
			regroupe = setTimeout(() => {
				regroupe = null
				dispatch(fetchDepVehicules())
				dispatch(fetchDispatchEnCours())
			}, 600)
		})
		return () => {
			clearInterval(interval)
			clearTimeout(regroupe)
			desabonner()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Fermeture du popover : clic hors du bandeau ou Échap.
	useEffect(() => {
		if (popoverId === null) return
		const onClick = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target))
				setPopoverId(null)
		}
		const onKey = (e) => {
			if (e.key === "Escape") setPopoverId(null)
		}
		document.addEventListener("mousedown", onClick)
		document.addEventListener("keydown", onKey)
		return () => {
			document.removeEventListener("mousedown", onClick)
			document.removeEventListener("keydown", onKey)
		}
	}, [popoverId])

	const secteurParCode = useMemo(
		() => buildSecteurParCode(secteurs),
		[secteurs],
	)

	// Missions actives (30 dispatchée / 50 en cours) par dépanneur.
	const missionsParDepanneur = useMemo(() => {
		const map = {}
		;(depsEnCours || []).forEach((dm) => {
			if (!dm.depanneur) return
			if (!map[dm.depanneur]) map[dm.depanneur] = []
			map[dm.depanneur].push(dm)
		})
		return map
	}, [depsEnCours])

	// Mission « à raconter » pour un dépanneur : la 50 la plus avancée
	// (dernier jalon max), sinon la 30 la plus ancienne (file d'attente).
	const missionCourante = (depanneurId) => {
		const missions = missionsParDepanneur[depanneurId] || []
		const enCours = missions.filter((dm) => dm.etat === DEP_STATE_ONGOING)
		if (enCours.length > 0)
			return enCours.reduce((a, b) =>
				dernierJalon(b) > dernierJalon(a) ? b : a,
			)
		// Le backend trie par -cree_le → la plus ancienne est en dernier.
		return missions.length > 0 ? missions[missions.length - 1] : null
	}

	// Couloir + données d'affichage d'un camion :
	// { lane, kind?, zone?, depuis? } — kind pour « En mission » (recue/route/
	// place), depuis (brut sérialisé, pour le tri) pour un détenteur dispo.
	const infoCamion = (c) => {
		if (c.indisponible || c.status !== STATUS_FONCTIONNEL)
			return { lane: "hs" }
		if (!c.occupe_par) return { lane: "libre", depuis: null, zone: null }
		const dm = missionCourante(c.occupe_par)
		if (dm) {
			const jalon = dernierJalon(dm)
			if (jalon === -1)
				return {
					lane: "mission",
					kind: "recue",
					zone: getZoneLabel(secteurParCode, dm.lieu_intervention, [
						dm.lieu_intervention_0,
						dm.lieu_intervention_1,
						dm.lieu_intervention_2,
					]),
				}
			const versLieu = jalon <= 1
			const cible = versLieu
				? {
						po: dm.lieu_intervention,
						textes: [
							dm.lieu_intervention_0,
							dm.lieu_intervention_1,
							dm.lieu_intervention_2,
						],
					}
				: cibleApresLieu(dm)
			return {
				lane: "mission",
				kind: jalon === 0 || jalon === 2 ? "route" : "place",
				zone: getZoneLabel(secteurParCode, cible.po, cible.textes),
			}
		}
		// Pas de mission active : dispo depuis sa DERNIÈRE ACTIVITÉ — dernier
		// jalon posé, sinon mission close au bureau (dispo serveur), sinon la
		// prise du camion si elle est plus récente (matin au dépôt) — dans ce
		// dernier cas sans zone (on ne sait pas où il est).
		const dispo = c.dispo
		const dispoValide =
			dispo &&
			dispo.depuis &&
			(!c.occupe_depuis ||
				!parseTs(dispo.depuis).isBefore(parseTs(c.occupe_depuis)))
		if (dispoValide)
			return {
				lane: "libre",
				depuis: dispo.depuis,
				zone: getZoneLabel(
					secteurParCode,
					dispo.lieu,
					dispo.lieu_textes,
				),
			}
		return { lane: "libre", depuis: c.occupe_depuis || null, zone: null }
	}

	// Camions enrichis puis répartis par couloir, avec leurs tris : En mission
	// reçue → en direction → sur place ; Libres par ancienneté de dispo (le
	// prochain à envoyer d'abord), camions sans détenteur en fin.
	const parLane = useMemo(() => {
		const enriched = (depVehicules || []).map((c) => ({
			...c,
			info: infoCamion(c),
		}))
		const lanes = { mission: [], libre: [], hs: [] }
		enriched.forEach((c) => lanes[c.info.lane].push(c))
		lanes.mission.sort(
			(a, b) => KIND_ORDER[a.info.kind] - KIND_ORDER[b.info.kind],
		)
		lanes.libre.sort((a, b) => {
			if (!a.info.depuis || !b.info.depuis)
				return (a.info.depuis ? 0 : 1) - (b.info.depuis ? 0 : 1)
			return (
				parseTs(a.info.depuis).valueOf() -
				parseTs(b.info.depuis).valueOf()
			)
		})
		return lanes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [depVehicules, depsEnCours, secteurParCode])

	const total =
		parLane.mission.length + parLane.libre.length + parLane.hs.length
	if (total === 0) return null

	// ---- rendu d'une puce ----

	const avatar = (c) => {
		if (c.info.lane === "hs")
			return (
				<span style={{ ...st.avatar, ...st.avatarHs }}>
					<FontAwesomeIcon icon={motifHs(c).icon} />
				</span>
			)
		if (c.occupe_par)
			return (
				<span
					style={{
						...st.avatar,
						...(c.info.kind === "recue" ? st.avatarRecue : {}),
					}}
				>
					{initiales(c.occupe_par_nom)}
				</span>
			)
		return <span style={{ ...st.avatar, ...st.avatarLibre }}>–</span>
	}

	// Statut compact : « reçue · zone » / « → zone » / « à zone » /
	// « HH:MM · zone » (dispo depuis) / « dispo » / « En réparation ».
	const statutCourt = (c) => {
		const { kind, zone, depuis, lane } = c.info
		if (lane === "hs")
			return (
				<span style={{ color: motifHs(c).color, fontWeight: 600 }}>
					{motifHs(c).texte}
				</span>
			)
		if (lane === "mission") {
			if (kind === "recue")
				return (
					<span style={st.zoneTxt}>
						<span
							style={{ color: COLORS.warning, fontWeight: 600 }}
						>
							reçue
						</span>
						{zone ? ` · ${zone}` : ""}
					</span>
				)
			return (
				<span style={st.zoneTxt}>
					<span style={{ color: COLORS.accent, fontWeight: 700 }}>
						{kind === "route" ? "→" : "à"}
					</span>{" "}
					<span style={{ color: COLORS.text, fontWeight: 600 }}>
						{zone || "…"}
					</span>
				</span>
			)
		}
		if (depuis)
			return (
				<span style={st.zoneTxt}>
					<span style={{ color: COLORS.success, fontWeight: 600 }}>
						{formatHeure(depuis)}
					</span>
					{zone ? ` · ${zone}` : ""}
				</span>
			)
		return (
			<span style={{ color: COLORS.success, fontWeight: 600 }}>
				dispo
			</span>
		)
	}

	// Statut long du popover (mêmes données, en toutes lettres).
	const statutLong = (c) => {
		const { kind, zone, depuis, lane } = c.info
		if (lane === "hs") return motifHs(c).texte
		if (lane === "mission")
			return KIND_LONG[kind] + (zone ? ` · ${zone}` : "")
		if (depuis)
			return `Dispo depuis ${formatHeure(depuis)}${zone ? ` · ${zone}` : ""}`
		return "Libre"
	}

	const chip = (c) => {
		const hs = c.info.lane === "hs"
		const { modele, plaques } = parseNomCamion(c.nom)
		const open = popoverId === c.id
		return (
			<div key={`camion_${c.id}`} style={st.chipWrap}>
				<button
					type="button"
					style={{ ...st.chip, ...(hs ? st.chipHs : {}) }}
					onClick={() => setPopoverId(open ? null : c.id)}
					aria-expanded={open}
					title={modele}
				>
					{avatar(c)}
					{/* QA Jordan 27.10 : « marque modèle » d'abord ; la plaque
					    seulement sans marque ni modèle (bulle : les deux). */}
					{modele ? (
						<span
							style={{ ...st.nomCamion, ...(hs ? st.nomHs : {}) }}
						>
							{modele}
						</span>
					) : (
						<span
							style={{ ...st.plaque, ...(hs ? st.plaqueHs : {}) }}
						>
							{plaques}
						</span>
					)}
					{statutCourt(c)}
				</button>
				{open && (
					<div style={st.popover} role="tooltip">
						<div style={st.popoverTitre}>
							{modele}
							{plaques ? ` · ${plaques}` : ""}
						</div>
						{c.occupe_par ? (
							<div style={st.popoverLigne}>
								<span
									style={{
										color: COLORS.text,
										fontWeight: 600,
									}}
								>
									{c.occupe_par_nom}
								</span>
								{c.occupe_depuis
									? ` · camion pris à ${formatHeure(c.occupe_depuis)}`
									: ""}
							</div>
						) : null}
						<div style={st.popoverLigne}>{statutLong(c)}</div>
					</div>
				)}
			</div>
		)
	}

	// ---- rendu d'un couloir ----

	const lane = ({ key, label }) => {
		const camions = parLane[key]
		if (camions.length === 0 || !actifs[key]) return null
		const shown = deplies[key] ? camions : camions.slice(0, LANE_CAP)
		const masques = camions.length - shown.length
		return (
			<div key={key} style={st.lane}>
				<div style={st.laneLabel}>
					{label}{" "}
					<span style={{ color: COLORS.textMuted }}>
						{camions.length}
					</span>
				</div>
				<div style={st.laneChips}>
					{shown.map(chip)}
					{masques > 0 && (
						<button
							type="button"
							style={st.plusBtn}
							onClick={() =>
								setDeplies({ ...deplies, [key]: true })
							}
						>
							+{masques}
						</button>
					)}
				</div>
			</div>
		)
	}

	// Pastille-compteur = toggle d'ouverture de son couloir.
	const countPill = (key, label, n, couleur) =>
		n > 0 && (
			<button
				type="button"
				style={{
					...st.countPill,
					...(actifs[key] ? st.countPillActive : {}),
				}}
				onClick={() =>
					setActifs((prev) => ({ ...prev, [key]: !prev[key] }))
				}
				aria-pressed={!!actifs[key]}
				aria-expanded={!!actifs[key]}
			>
				<span style={{ ...st.countDot, backgroundColor: couleur }} />
				<span style={{ color: COLORS.text }}>{n}</span> {label}
			</button>
		)

	return (
		<section
			ref={rootRef}
			className="dep-dispatch-v2"
			style={st.flotte}
			aria-label="Occupation des camions"
		>
			<div style={st.header}>
				<span style={st.titre}>
					<span className="dispatch-camions-dot" style={st.dot} />
					Camions
				</span>
				{countPill(
					"mission",
					"en mission",
					parLane.mission.length,
					COLORS.accent,
				)}
				{countPill(
					"libre",
					parLane.libre.length > 1 ? "libres" : "libre",
					parLane.libre.length,
					COLORS.success,
				)}
				{countPill(
					"hs",
					"hors service",
					parLane.hs.length,
					COLORS.warning,
				)}
			</div>
			{LANES.map(lane)}
		</section>
	)
}

const st = {
	flotte: { marginTop: 20 },
	header: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		flexWrap: "wrap",
		marginBottom: 10,
	},
	titre: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		color: COLORS.textLight,
	},
	dot: {
		width: 7,
		height: 7,
		borderRadius: "50%",
		backgroundColor: COLORS.success,
	},
	countPill: {
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
		// Bordure en longhands (pas le shorthand `border`) : l'état actif
		// surcharge borderColor, et au retour inactif React efface le longhand
		// sans re-poser un shorthand inchangé → la couleur retomberait sur
		// currentColor.
		borderWidth: 1,
		borderStyle: "solid",
		borderColor: COLORS.border,
		backgroundColor: COLORS.surface,
		borderRadius: 99,
		padding: "4px 12px",
		fontFamily: "inherit",
		fontSize: 12.5,
		fontWeight: 600,
		color: COLORS.textMuted,
		cursor: "pointer",
		fontVariantNumeric: "tabular-nums",
	},
	countPillActive: {
		borderColor: COLORS.text,
		color: COLORS.text,
		boxShadow: `inset 0 0 0 1px ${COLORS.text}`,
	},
	countDot: { width: 6, height: 6, borderRadius: "50%" },
	lane: { display: "flex", gap: 14, marginBottom: 8 },
	laneLabel: {
		flex: "0 0 96px",
		paddingTop: 7,
		fontSize: 10.5,
		fontWeight: 700,
		letterSpacing: "0.09em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		fontVariantNumeric: "tabular-nums",
		borderRight: `1px solid ${COLORS.border}`,
	},
	laneChips: {
		display: "flex",
		flexWrap: "wrap",
		gap: 6,
		alignContent: "flex-start",
		minWidth: 0,
	},
	chipWrap: { position: "relative" },
	chip: {
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
		height: 30,
		padding: "0 10px 0 6px",
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		fontFamily: "inherit",
		fontSize: 12,
		cursor: "pointer",
		whiteSpace: "nowrap",
		boxSizing: "border-box",
	},
	chipHs: { backgroundColor: COLORS.disabledBg },
	avatar: {
		width: 20,
		height: 20,
		borderRadius: "50%",
		backgroundColor: COLORS.accentLight,
		color: COLORS.accent,
		fontSize: 9.5,
		fontWeight: 700,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		boxSizing: "border-box",
	},
	avatarRecue: {
		backgroundColor: COLORS.warningLight,
		color: COLORS.warning,
	},
	avatarLibre: {
		backgroundColor: "transparent",
		border: `1.5px dashed ${COLORS.borderStrong}`,
		color: COLORS.textLight,
	},
	avatarHs: {
		backgroundColor: "transparent",
		color: COLORS.textLight,
		fontSize: 11,
	},
	// Immatriculation en « plaque » — même traitement que les cartes mission.
	// Bordure en longhands : plaqueHs surcharge borderColor (cf. countPill).
	plaque: {
		flexShrink: 0,
		padding: "0 4px",
		borderWidth: 1.5,
		borderStyle: "solid",
		borderColor: COLORS.text,
		borderRadius: 4,
		fontSize: 10,
		fontWeight: 800,
		lineHeight: "14px",
		letterSpacing: "0.06em",
		whiteSpace: "nowrap",
		fontVariantNumeric: "tabular-nums",
		color: COLORS.text,
	},
	plaqueHs: { borderColor: COLORS.textLight, color: COLORS.textLight },
	nomCamion: {
		maxWidth: 140,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontWeight: 700,
		color: COLORS.text,
	},
	nomHs: { color: COLORS.textLight },
	zoneTxt: { color: COLORS.textMuted, fontVariantNumeric: "tabular-nums" },
	plusBtn: {
		height: 30,
		padding: "0 12px",
		border: `1.5px dashed ${COLORS.borderStrong}`,
		backgroundColor: "transparent",
		borderRadius: 8,
		fontFamily: "inherit",
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		cursor: "pointer",
	},
	popover: {
		position: "absolute",
		bottom: "calc(100% + 8px)",
		left: 0,
		zIndex: 20,
		padding: "10px 14px",
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
		whiteSpace: "nowrap",
	},
	popoverTitre: { fontSize: 13, fontWeight: 700, color: COLORS.text },
	popoverLigne: {
		fontSize: 12,
		color: COLORS.textMuted,
		marginTop: 2,
		fontVariantNumeric: "tabular-nums",
	},
}

export default DepDispatchCamions
